import { ShieldCheck } from "lucide-react";
import { useUserXp } from "@/hooks/use-user-xp";
import { useLocale } from "@/hooks/use-locale";
import { localizedRankName, rankProgressCopy } from "@/lib/homepage-locales";

/**
 * The reader's ego corner: their own rank title, how many discoveries they've
 * certified, and how close the next title is. Never compared to anyone else.
 */
export function RankChip() {
  const { hydrated, xp, rank, certifiedCount, quizCount } = useUserXp();
  const [lang] = useLocale();

  if (!hydrated) return <div className="h-7 w-16" aria-hidden />;

  const wins = certifiedCount + quizCount;
  return (
    <div
      aria-label="Your Smartasaurus rank and progress"
      title="Your discoveries and quiz progress"
      className="inline-flex min-w-0 shrink items-center gap-2 rounded-full border border-border bg-card/60 py-1 pl-1.5 pr-3 transition hover:border-primary"
    >
      <span className="relative grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/15">
        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
        <span className="absolute -bottom-1 -right-1 rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground tabular-nums">
          {rank.index + 1}
        </span>
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-[11px] font-bold uppercase tracking-wider text-foreground">
          <span data-no-translate>{localizedRankName(lang, rank.current.name)}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="relative h-1 w-10 overflow-hidden rounded-full bg-border/70">
            <span
              className="absolute inset-y-0 left-0 rounded-full bg-primary transition-all duration-700"
              style={{ width: `${Math.round(rank.progress * 100)}%` }}
            />
          </span>
          <span data-no-translate className="truncate text-[10px] tabular-nums text-muted-foreground">
            {(() => {
              const c = rankProgressCopy(lang);
              const tail = rank.next ? c.toNext(rank.toNext, localizedRankName(lang, rank.next.name)) : c.top;
              return wins > 0 ? `${c.wins(wins)} · ${tail}` : tail;
            })()}
          </span>
        </span>
      </span>
    </div>
  );
}
